/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import { useCallback, useEffect, useRef, useState } from "react";

import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import {
  CallClient,
  TeamsCallAgent,
  TeamsCall,
} from "@azure/communication-calling";

import qs from "query-string";
import { useLocation, useNavigate } from "react-router-dom";

import TeamsCallModal from "./TeamsCallModal";

import { useCheckinAccessNavigation } from "../../../lib/hooks/useCheckinAccessNavigation";
import { useCallSounds } from "../../../lib/hooks/useCallSounds";
import { useRingTimeout } from "../../../lib/hooks/useRingTimeout";

const TeamsAudioCall = () => {
  const navigate = useNavigate();

  const { search } = useLocation();
  const query = qs.parse(search);
  const type = query.type as string;

  const {
    handleCheckinAccessNavigation,
    willNavigateToCheckinAccessControlPage,
    isDoorOpening,
    accessSwitchModal,
  } = useCheckinAccessNavigation({ type });

  const callerAcsToken = query.callerAcsToken as string;
  const targetTeamsUserIds = query.targetTeamsUserIds as string;
  const targetTeamsUserId = targetTeamsUserIds?.split(",")[0];

  const [triggerCall, setTriggerCall] = useState<boolean>(false);
  const [teamsCallAgent, setTeamsCallAgent] = useState<TeamsCallAgent | null>(
    null,
  );
  const [call, setCall] = useState<TeamsCall | null>(null);
  const callRef = useRef<TeamsCall | null>(null);
  const [callingStatus, setCallingStatus] =
    useState<TeamsCallStatus>("connecting");

  // Keeps the hang-up we do ourselves from being reported as a normal ending.
  const hasTimedOutRef = useRef<boolean>(false);

  const {
    startSound,
    ringingSound,
    connectedSound,
    endSound,
    playSound,
    stopAllSounds,
  } = useCallSounds();

  useEffect(() => {
    callRef.current = call;
  }, [call]);

  const startCall = useCallback(async () => {
    if (!teamsCallAgent || !targetTeamsUserId) {
      console.log(
        "Cannot start call: Target Teams User Id is missing or Teams Call Agent not initialized.",
      );
      setCallingStatus("error");
      return;
    }

    try {
      setCallingStatus("connecting");

      playSound(startSound);

      const call = teamsCallAgent.startCall({
        microsoftTeamsUserId: targetTeamsUserId.trim(),
      });

      setCall(call);

      call.on("stateChanged", () => {
        console.log("Call state : ", call.state);

        switch (call.state) {
          case "Connecting":
            playSound(startSound);
            setCallingStatus("connecting");
            break;
          case "Ringing":
            playSound(ringingSound);
            setCallingStatus("ringing");
            break;
          case "Connected":
            playSound(connectedSound);
            setCallingStatus("connected");
            break;
          case "LocalHold":
          case "RemoteHold":
            setCallingStatus("connected");
            break;
          case "Disconnected":
            playSound(endSound);

            if (!hasTimedOutRef.current) {
              setCallingStatus("disconnected");
            }
            break;
          default:
            break;
        }
      });

      console.log("Call started.");
    } catch (error) {
      console.log("Failed to start call: ", error);
      setCallingStatus("error");
    }
  }, [teamsCallAgent, targetTeamsUserId]);

  const handleRingTimeout = useCallback(async () => {
    console.log("The call was never answered. Hanging up.");

    hasTimedOutRef.current = true;
    setCallingStatus("noAnswer");

    try {
      if (callRef.current?.state !== "Disconnected") {
        await callRef.current?.hangUp();
      }
    } catch (error) {
      console.log("Error hanging up unanswered call:", error);
    }
  }, []);

  useRingTimeout(callingStatus === "ringing", handleRingTimeout);

  const hangUpCall = useCallback(async () => {
    if (!call) {
      handleCheckinAccessNavigation();
      return;
    }

    try {
      await call.hangUp();
      setCallingStatus("disconnected");
    } catch (error) {
      console.log("Error hanging up call:", error);
      setCallingStatus("error");
    }
  }, [call]);

  const onClose = useCallback(() => {
    handleCheckinAccessNavigation();
  }, []);

  useEffect(() => {
    if (triggerCall) {
      startCall();
    }
  }, [triggerCall, startCall]);

  useEffect(() => {
    const initializeCallAgent = async () => {
      if (!callerAcsToken) {
        handleCheckinAccessNavigation();
        return;
      }

      try {
        setCallingStatus("connecting");

        const client = new CallClient();
        const tokenCredential = new AzureCommunicationTokenCredential(
          callerAcsToken.trim(),
        );
        const agent = await client.createTeamsCallAgent(tokenCredential);
        setTeamsCallAgent(agent);

        const deviceManager = await client.getDeviceManager();
        await deviceManager.askDevicePermission({ video: true, audio: true });

        console.log("Call agent initialized.");
        setTriggerCall(true);
      } catch (error) {
        console.log("Failed to initialize call agent:", error);
        setCallingStatus("error");
      }
    };

    initializeCallAgent();
  }, [callerAcsToken, navigate]);

  useEffect(() => {
    if (
      callingStatus === "disconnected" ||
      callingStatus === "error" ||
      callingStatus === "noAnswer"
    ) {
      setTimeout(() => {
        handleCheckinAccessNavigation();
      }, 5000);
    }
  }, [callingStatus]);

  useEffect(() => {
    return () => {
      stopAllSounds();
      (async () => {
        if (callRef.current?.state !== "Disconnected") {
          await callRef.current?.hangUp();
        }
        setCall(null);
      })();
    };
  }, []);

  if (isDoorOpening) {
    return accessSwitchModal;
  }

  return (
    <TeamsCallModal
      status={callingStatus}
      onEndCall={hangUpCall}
      onClose={onClose}
      willNavigateToAccessControl={willNavigateToCheckinAccessControlPage}
    />
  );
};

export default TeamsAudioCall;
